import React from "react";
import {
  KarlovarskyKraj,
  JihomoravskyKraj,
  JihoceskyKraj,
  KralovohradeckyKraj,
  LibereckyKraj,
  MoravskoslezskyKraj,
  OlomouckyKraj,
  PardubickyKraj,
  PlzenskyKraj,
  Praha,
  StredoceskyKraj,
  UsteckyKraj,
  Vysocina,
  ZlinskyKraj,
} from "@/components/map/regions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import type { TranslationKey } from "@/i18n/messages";

enum Region {
  CentralBohemia = "central-bohemia",
  SouthBohemia = "south-bohemia",
  Plzen = "plzen",
  KarlovyVary = "karlovy-vary",
  Liberec = "liberec",
  HradecKralove = "hradec-kralove",
  Pardubice = "pardubice",
  SouthMoravia = "south-moravia",
  MoraviaSilesia = "moravia-silesia",
  Praha = "praha",
  Vysocina = "vysocina",
  Olomouc = "olomouc",
  Zlinsky = "zlinsky",
  Ustecky = "ustecky",
}

export type RegionId =
  | "central-bohemia"
  | "south-bohemia"
  | "plzen"
  | "karlovy-vary"
  | "liberec"
  | "hradec-kralove"
  | "pardubice"
  | "south-moravia"
  | "moravia-silesia"
  | "praha"
  | "vysocina"
  | "olomouc"
  | "zlinsky"
  | "ustecky";

type RegionType = {
  id: Region;
  nameKey: TranslationKey;
};

export type BranchMapMarker = {
  id: string;
  name: string;
  region?: string;
  regionId?: RegionId;
  latitude: number;
  longitude: number;
  score: number | null;
  totalImages: number;
};

type CzechRepublicMapProps = Omit<
  React.SVGAttributes<SVGSVGElement>,
  "onClick"
> & {
  markers?: BranchMapMarker[];
  regionTints?: Partial<Record<RegionId, string>>;
  onMarkerClick?: (marker: BranchMapMarker) => void;
};

const regions: RegionType[] = [
  { id: Region.CentralBohemia, nameKey: "region.centralBohemia" },
  { id: Region.SouthBohemia, nameKey: "region.southBohemia" },
  { id: Region.Plzen, nameKey: "region.plzen" },
  { id: Region.KarlovyVary, nameKey: "region.karlovyVary" },
  { id: Region.Liberec, nameKey: "region.liberec" },
  { id: Region.HradecKralove, nameKey: "region.hradecKralove" },
  { id: Region.Pardubice, nameKey: "region.pardubice" },
  { id: Region.SouthMoravia, nameKey: "region.southMoravia" },
  { id: Region.MoraviaSilesia, nameKey: "region.moraviaSilesia" },
  { id: Region.Praha, nameKey: "region.praha" },
  { id: Region.Vysocina, nameKey: "region.vysocina" },
  { id: Region.Olomouc, nameKey: "region.olomouc" },
  { id: Region.Zlinsky, nameKey: "region.zlinsky" },
  { id: Region.Ustecky, nameKey: "region.ustecky" },
];

const regionComponents = {
  [Region.CentralBohemia]: StredoceskyKraj,
  [Region.SouthBohemia]: JihoceskyKraj,
  [Region.Plzen]: PlzenskyKraj,
  [Region.KarlovyVary]: KarlovarskyKraj,
  [Region.Liberec]: LibereckyKraj,
  [Region.HradecKralove]: KralovohradeckyKraj,
  [Region.Pardubice]: PardubickyKraj,
  [Region.SouthMoravia]: JihomoravskyKraj,
  [Region.MoraviaSilesia]: MoravskoslezskyKraj,
  [Region.Praha]: Praha,
  [Region.Vysocina]: Vysocina,
  [Region.Olomouc]: OlomouckyKraj,
  [Region.Zlinsky]: ZlinskyKraj,
  [Region.Ustecky]: UsteckyKraj,
};

const CZ_BOUNDS = {
  minLat: 48.55,
  maxLat: 51.1,
  minLng: 12.0,
  maxLng: 18.95,
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const projectCoordinates = (latitude: number, longitude: number) => {
  const x =
    ((clamp(longitude, CZ_BOUNDS.minLng, CZ_BOUNDS.maxLng) -
      CZ_BOUNDS.minLng) /
      (CZ_BOUNDS.maxLng - CZ_BOUNDS.minLng)) *
    1000;
  const y =
    ((CZ_BOUNDS.maxLat -
      clamp(latitude, CZ_BOUNDS.minLat, CZ_BOUNDS.maxLat)) /
      (CZ_BOUNDS.maxLat - CZ_BOUNDS.minLat)) *
    570;

  return { x, y };
};

export const getScoreTintColor = (score: number | null) => {
  if (score === null) return "#94a3b8";
  if (score >= 95) return "#16a34a";
  if (score >= 85) return "#f59e0b";
  return "#dc2626";
};

const CzechRepublicMap = React.forwardRef<SVGSVGElement, CzechRepublicMapProps>(
  (
    { className, markers = [], regionTints, onMarkerClick, ...props },
    ref,
  ) => {
    const { t, tp, formatNumber } = useLocalization();

    const formatScore = (score: number | null) =>
      score === null
        ? t("common.noData")
        : formatNumber(score / 100, {
            style: "percent",
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });

    return (
      <svg
        ref={ref}
        className={cn("h-full w-full", className)}
        fill="#dbe4ec"
        height="570"
        stroke="#ffffff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth=".7"
        version="1.2"
        viewBox="0 0 1000 570"
        width="1000"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <TooltipProvider>
          {regions.map((region) => {
            const RegionComponent = regionComponents[region.id];
            const tint = regionTints?.[region.id];
            return (
              <Tooltip key={region.id}>
                <TooltipTrigger asChild>
                  <RegionComponent
                    className="transition-opacity hover:opacity-80 hover:drop-shadow-xl"
                    style={{
                      fill: tint ?? "#dbe4ec",
                      fillOpacity: tint ? 0.55 : 1,
                      cursor: "default",
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent align="center">
                  <p>{t(region.nameKey)}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}

          <g id="branch-markers">
            {markers.map((marker) => {
              const { x, y } = projectCoordinates(
                marker.latitude,
                marker.longitude,
              );

              return (
                <Tooltip key={marker.id}>
                  <TooltipTrigger asChild>
                    <g
                      className="cursor-pointer outline-none transition-opacity hover:opacity-90"
                      onClick={() => onMarkerClick?.(marker)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onMarkerClick?.(marker);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      transform={`translate(${x} ${y})`}
                    >
                      <path
                        d="M0 17.5C-3.8 12.7-12 1.7-12-6.3-12-13-6.6-18.5 0-18.5S12-13 12-6.3c0 8-8.2 19-12 23.8Z"
                        fill={getScoreTintColor(marker.score)}
                        stroke="#ffffff"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        style={{
                          filter:
                            "drop-shadow(0 3px 5px rgba(15, 23, 42, 0.28))",
                        }}
                      />
                      <circle
                        cx="0"
                        cy="-6.3"
                        r="4"
                        fill="#ffffff"
                        pointerEvents="none"
                      />
                    </g>
                  </TooltipTrigger>
                  <TooltipContent align="center" className="space-y-1">
                    <p className="font-medium">{marker.name}</p>
                    {marker.region && (
                      <p className="text-muted-foreground">{marker.region}</p>
                    )}
                    <p>
                      {t("common.score")}: {formatScore(marker.score)}
                    </p>
                    <p>
                      {tp("map.markerPhotos", marker.totalImages, {
                        count: formatNumber(marker.totalImages),
                      })}
                    </p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </g>
        </TooltipProvider>
      </svg>
    );
  },
);

CzechRepublicMap.displayName = "CzechRepublicMap";

export default CzechRepublicMap;
